/*
 * Hearth Court chat widget — vanilla JS, no dependencies, no build step.
 *
 * Config (set before this script loads, or leave the defaults):
 *   window.HC_CHAT_ENDPOINT — absolute URL of the /api/chat function.
 *   window.HC_LEAD_ENDPOINT — absolute URL of the /api/lead function
 *                             (default: same host as the chat endpoint).
 *
 * Notes for whoever edits this next:
 *  - Bot text is inserted with textContent, never innerHTML. The system prompt
 *    tells the model to answer in plain prose for exactly this reason. Do not
 *    "improve" this by rendering markdown without sanitizing.
 *  - Transcript lives in memory only. It goes to /api/chat on every turn, and to
 *    /api/lead only when the shopper fills in and sends the optional contact
 *    form (first name, email, optional phone, the separate consent box, and the
 *    chat so far, which the form tells them). Closing the panel keeps the
 *    transcript; a reload clears it. The only things stored in the browser are
 *    four sessionStorage flags: the contact ask was shown, a lead was sent, the
 *    chat was opened, and the help prompt was shown.
 *  - The help prompt is one small bubble above the launcher: product pages only,
 *    once per visit, after 90 seconds of the page in view, and only if the chat
 *    was never opened this visit. It is a plain element inside the widget, never
 *    a native dialog: showModal puts a dialog in the top layer, makes the rest of
 *    the page inert and hides every other overlay (Shopify's spam-check puzzle
 *    hid behind the quote panel that way on Oct 27). For the same reason it
 *    waits while any dialog, drawer or modal is open, since it would sit unseen
 *    under the top layer, and it steps aside while it would cover a buy button.
 *  - The contact ask is scripted here, not said by the model, and it is never
 *    added to history: the API history must stay user/assistant alternating.
 *  - The model itself asks for a first name and email when a shopper wants a
 *    quote, stock or a lead time (system prompt rail 4), and shoppers answer in
 *    the chat. So a name, email or phone typed into the chat is copied into the
 *    form, the form moves under the answer with one line saying to press Send,
 *    and nothing is sent until the shopper presses Send.
 *  - The contact parsing block between the contact-parse markers is read and run
 *    by scripts/lead.test.mjs. Keep it self-contained (plain functions, no DOM).
 */
(function () {
  "use strict";

  if (window.__hcChatLoaded) return;
  window.__hcChatLoaded = true;

  var ENDPOINT = window.HC_CHAT_ENDPOINT || "https://hearthcourt-chatbot.vercel.app/api/chat";
  var MAX_CHARS = 2000;

  var GREETING =
    "Hi — I'm Hearth Court's assistant. I can help with our fireplaces, " +
    "shipping and returns, and how ordering works. What can I help you with?";

  var OFFLINE =
    "I can't reach our system right now. Email support@hearthcourt.com or " +
    "call or text (307) 317-1030 and the team will help — we're in Monday " +
    "through Friday, 9 to 6 Eastern.";

  /** Conversation history in Claude API shape. */
  var history = [];
  var busy = false;

  // ------------------------------------------------------------------ DOM

  var root = document.createElement("div");
  root.className = "hc-chat";
  root.innerHTML = [
    '<div class="hc-chat__panel" role="dialog" aria-modal="false" aria-label="Hearth Court chat">',
    '  <div class="hc-chat__header">',
    "    <div>",
    '      <p class="hc-chat__title">Hearth Court</p>',
    '      <p class="hc-chat__subtitle">Mon–Fri, 9–6 ET</p>',
    "    </div>",
    '    <button type="button" class="hc-chat__close" aria-label="Close chat">×</button>',
    "  </div>",
    '  <div class="hc-chat__log" role="log" aria-live="polite" aria-atomic="false"></div>',
    '  <form class="hc-chat__form">',
    '    <textarea class="hc-chat__input" rows="1" placeholder="Ask about a fireplace, shipping, returns…"',
    '      aria-label="Your message" maxlength="' + MAX_CHARS + '"></textarea>',
    '    <button type="submit" class="hc-chat__send" aria-label="Send message">',
    '      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>',
    "    </button>",
    "  </form>",
    '  <p class="hc-chat__legal">AI assistant. It can make mistakes, so confirm the details with us before you order. For installation or code questions, ask a licensed installer and your local inspector.</p>',
    "</div>",
    '<button type="button" class="hc-chat__launcher" aria-label="Open chat with Hearth Court">',
    '  <svg viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round"',
    '    d="M21 11.5a8.4 8.4 0 01-9 8.4 9 9 0 01-3.9-.9L3 21l1.9-4.6A8.4 8.4 0 013 11.5a8.4 8.4 0 018.9-8.4 8.4 8.4 0 019.1 8.4z"/></svg>',
    "</button>",
  ].join("\n");

  var panel = root.querySelector(".hc-chat__panel");
  var log = root.querySelector(".hc-chat__log");
  var form = root.querySelector(".hc-chat__form");
  var input = root.querySelector(".hc-chat__input");
  var sendBtn = root.querySelector(".hc-chat__send");
  var launcher = root.querySelector(".hc-chat__launcher");
  var closeBtn = root.querySelector(".hc-chat__close");

  // ------------------------------------------------------------- rendering

  function bubble(text, kind) {
    var el = document.createElement("div");
    el.className = "hc-chat__msg hc-chat__msg--" + kind;
    el.textContent = text; // never innerHTML
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function showTyping() {
    var el = document.createElement("div");
    el.className = "hc-chat__typing";
    el.setAttribute("aria-label", "Assistant is typing");
    el.innerHTML = "<span></span><span></span><span></span>";
    log.appendChild(el);
    log.scrollTop = log.scrollHeight;
    return el;
  }

  function setBusy(state) {
    busy = state;
    sendBtn.disabled = state;
    input.disabled = state;
  }

  // --------------------------------------------------------- contact ask
  //
  // Once per visit the assistant offers to have the team send an exact quote:
  // after the shopper's first answered question, or after the panel has been
  // open for 75 seconds on a product page. A small inline form, skippable,
  // never blocking: the composer keeps working the whole time.

  var LEAD_ENDPOINT =
    window.HC_LEAD_ENDPOINT || ENDPOINT.replace(/\/api\/chat\/?$/, "/api/lead");
  var ASK_AFTER_MS = 75000;
  var ASK_KEY = "hcChatLeadAsked";
  var SENT_KEY = "hcChatLeadSent";

  var ASK_TEXT =
    "Would you like an exact quote? Leave your first name, your email and, if you " +
    "like, a phone number, and the team will send it. This is optional, and you can keep asking " +
    "questions either way.";

  /** Used instead of ASK_TEXT when the answer just above already asked for a first name and email. */
  var ASK_TEXT_SHORT =
    "You can leave your first name and email here, and the team will send your quote. " +
    "This is optional, and you can keep asking questions either way.";

  /** Shown when the shopper typed contact details into the chat: they are not sent until Send. */
  var CONFIRM_TEXT = "To pass these details to the team, check them below and press Send.";

  /** Word for word what api/lead.ts stores as consent_text. Change both together. */
  var CONSENT_LABEL =
    "Yes, Hearth Court may call or text me about this request, including with " +
    "an automated or AI assistant. Not required to buy.";

  var LEAD_FAIL =
    "That didn't go through. You can email support@hearthcourt.com or call or " +
    "text (307) 317-1030 instead.";

  var EMAIL_RE = /^[^\s@<>()[\]",;:]+@[^\s@<>()[\]",;:]+\.[A-Za-z]{2,}$/; // same as api/lead.ts

  // <contact-parse>
  /**
   * Finds an email, a US phone number and a first name in one chat message.
   * The name is taken only from a plain introduction ("my name is Sam", "I'm Sam")
   * or from one or two words placed right before the email ("Sam, sam@example.com").
   * Everything found is only a prefill: the shopper sees it and presses Send.
   */
  var EMAIL_FIND = /[^\s@<>()[\]",;:]+@[^\s@<>()[\]",;:]+\.[A-Za-z]{2,}/;
  // No lookbehind: older iPhone Safari cannot parse it, and one bad regex stops the whole widget.
  var PHONE_FIND = /(?:^|\D)((?:\+?1[\s.-]?)?(?:\(\s*[2-9]\d{2}\s*\)|[2-9]\d{2})[\s.-]?[2-9]\d{2}[\s.-]?\d{4})(?!\d)/;
  var NAME_WORD = "[A-Za-z][A-Za-z'\u2019-]{0,29}";
  var NAME_INTRO = new RegExp("\\b(?:my name is|my name's|name is|name:)\\s+(" + NAME_WORD + ")", "i");
  var NAME_IAM = new RegExp("\\b(?:I'm|I\u2019m|I am|This is|It's|It\u2019s)\\s+([A-Z][A-Za-z'\u2019-]{0,29})\\b");
  var NAME_LEAD = new RegExp(
    "^\\s*(" + NAME_WORD + ")(?:\\s+" + NAME_WORD + ")?(?:\\s*[,;:\u2013-]\\s*|\\s+)" +
      "(?:(?:my\\s+)?e-?mail(?:\\s+is)?\\s*[:-]?\\s*)?[^\\s@]+@"
  );
  var NOT_A_NAME = /^(?:sure|yes|yeah|yep|ok|okay|hi|hey|hello|thanks|thank|please|email|e-mail|mail|my|me|it|its|it's|here|is|at|on|in|and|the|a|an|to|phone|number|contact|reach|send|name|i|im|i'm|i\u2019m|not|no)$/i;

  function parseContact(text) {
    var out = { email: "", phone: "", name: "" };
    var s = String(text || "");
    var em = s.match(EMAIL_FIND);
    if (em) out.email = em[0];
    var ph = (em ? s.replace(em[0], " ") : s).match(PHONE_FIND);
    if (ph) out.phone = ph[1].trim();
    var m = s.match(NAME_INTRO) || s.match(NAME_IAM) || (em ? s.match(NAME_LEAD) : null);
    if (m && !NOT_A_NAME.test(m[1])) out.name = m[1].charAt(0).toUpperCase() + m[1].slice(1);
    return out;
  }
  // </contact-parse>

  function storeGet(k) {
    try { return window.sessionStorage.getItem(k); } catch (e) { return null; }
  }
  function storeSet(k, v) {
    try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode: ask once per page instead */ }
  }

  var asked = storeGet(ASK_KEY) === "1";
  var leadSent = storeGet(SENT_KEY) === "1"; // a lead went through this visit: no more forms
  var safetyAnswered = false; // a gas-smell answer was given: no ask and no form on this page, by any path
  var pendingAsk = false; // the 75 s mark passed while an answer was in flight
  var pendingOffer = null; // contact details typed into the chat while another answer was in flight
  var lastReply = null; // the latest assistant answer, to word the ask
  var askTimer = null;
  var openSince = 0;
  var openTotal = 0;
  var lead = null; // the inline form's elements while it is on screen

  function isProductPage() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    if (meta && meta.page && meta.page.pageType) return meta.page.pageType === "product";
    return /\/products\//.test(window.location.pathname);
  }

  function productTitle() {
    var h = document.querySelector("h1.product-title");
    var t = (h && h.textContent) || "";
    if (!t) {
      var og = document.querySelector('meta[property="og:title"]');
      t = (og && og.getAttribute("content")) || "";
    }
    return t.replace(/\s+/g, " ").trim().slice(0, 200);
  }

  function startAskClock() {
    if (asked || safetyAnswered || askTimer || !isProductPage()) return;
    openSince = Date.now();
    askTimer = setTimeout(function () {
      askTimer = null;
      openTotal += Date.now() - openSince;
      requestAsk();
    }, Math.max(0, ASK_AFTER_MS - openTotal));
  }

  function pauseAskClock() {
    if (!askTimer) return;
    clearTimeout(askTimer);
    askTimer = null;
    openTotal += Date.now() - openSince;
  }

  function requestAsk() {
    if (asked || safetyAnswered) return;
    if (busy) { pendingAsk = true; return; } // never cut into an answer in flight
    if (!root.classList.contains("hc-chat--open")) return;
    showAsk();
  }

  /**
   * Called once per turn, after the composer is enabled again. reply is null when the turn failed;
   * typed is what parseContact found in the shopper's message.
   */
  function afterTurn(reply, typed) {
    if (reply) lastReply = reply;
    // Never follow a safety answer (gas smell: leave, call 911) with an ask or a form, now or later on this page.
    if (reply && /\b911\b|leave the (?:building|house|home)/i.test(reply)) {
      safetyAnswered = true;
      pendingOffer = null;
      pauseAskClock();
      return;
    }
    if (safetyAnswered) return;

    var offer = typed && (typed.email || typed.phone) ? typed : pendingOffer;
    pendingOffer = null;
    if (offer) {
      // The shopper answered with their details in the chat (often after the assistant asked for them).
      setTimeout(function () { offerFromChat(offer); }, 900); // let the answer land first
      return;
    }

    if (asked) return;
    if (reply || pendingAsk) {
      pendingAsk = false;
      setTimeout(requestAsk, 900); // let the answer land first
    }
  }

  /** Puts the form, prefilled from the chat message, under the latest answer, with one line saying to press Send. */
  function offerFromChat(t) {
    if (leadSent || safetyAnswered) return;
    if (busy) { pendingOffer = t; return; } // a newer question is in flight; offer after its answer

    asked = true;
    storeSet(ASK_KEY, "1");
    pauseAskClock();

    if (!lead) lead = buildLeadForm();
    if (t.name && !lead.name.value) lead.name.value = t.name;
    if (t.email && !lead.email.value) lead.email.value = t.email;
    if (t.phone && !lead.phone.value) lead.phone.value = t.phone;

    if (log.lastChild !== lead.wrap) {
      var line = bubble(CONFIRM_TEXT, "bot");
      log.appendChild(lead.wrap); // moves a form already on screen down here
      lead.reveal(false);
      log.scrollTop += line.getBoundingClientRect().top - log.getBoundingClientRect().top - 8;
    } else {
      lead.reveal(false);
    }
    var next = !lead.name.value ? lead.name : !lead.email.value ? lead.email : lead.send;
    try { next.focus({ preventScroll: true }); } catch (e) { next.focus(); }
  }

  function showAsk() {
    asked = true;
    storeSet(ASK_KEY, "1");
    pauseAskClock();
    if (lead || leadSent) return; // a form is already on screen, or a lead already went through
    // When the answer just above already asked for a first name and email, do not ask the same question twice.
    var modelAsked = !!lastReply && /first name/i.test(lastReply) && /\bemail\b/i.test(lastReply);
    bubble(modelAsked ? ASK_TEXT_SHORT : ASK_TEXT, "bot");
    lead = buildLeadForm();
    log.appendChild(lead.wrap);
    log.scrollTop = log.scrollHeight;
  }

  function buildLeadForm() {
    var wrap = document.createElement("div");
    wrap.className = "hc-chat__lead";
    // Static markup only; nothing the shopper typed is ever set through innerHTML.
    // Two steps so the answer above stays in view on a phone: a one-line choice first,
    // the fields only after "Yes".
    wrap.innerHTML = [
      '<div class="hc-chat__lead-choice">',
      '  <button type="button" class="hc-chat__lead-yes">Yes, send me a quote</button>',
      '  <button type="button" class="hc-chat__lead-skip">No thanks</button>',
      "</div>",
      '<form class="hc-chat__lead-form" novalidate hidden>',
      '  <label class="hc-chat__lead-label" for="hc-lead-name">First name</label>',
      '  <input class="hc-chat__lead-input" id="hc-lead-name" name="first_name" type="text"',
      '    autocomplete="given-name" maxlength="60" required>',
      '  <label class="hc-chat__lead-label" for="hc-lead-email">Email</label>',
      '  <input class="hc-chat__lead-input" id="hc-lead-email" name="email" type="email"',
      '    autocomplete="email" inputmode="email" maxlength="254" required>',
      '  <label class="hc-chat__lead-label" for="hc-lead-phone">Phone (optional)</label>',
      '  <input class="hc-chat__lead-input" id="hc-lead-phone" name="phone" type="tel"',
      '    autocomplete="tel" inputmode="tel" maxlength="20">',
      '  <label class="hc-chat__lead-consent">',
      '    <input type="checkbox" name="consent"><span class="hc-chat__lead-consent-text"></span>',
      "  </label>",
      '  <input class="hc-chat__hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">',
      '  <p class="hc-chat__lead-error" role="alert" hidden></p>',
      '  <div class="hc-chat__lead-actions">',
      '    <button type="submit" class="hc-chat__lead-send">Send</button>',
      '    <button type="button" class="hc-chat__lead-skip">No thanks</button>',
      "  </div>",
      '  <p class="hc-chat__lead-note">Used only to reply about your quote. ' +
        "We attach this chat so you don't have to repeat yourself.</p>",
      "</form>",
    ].join("\n");
    wrap.querySelector(".hc-chat__lead-consent-text").textContent = CONSENT_LABEL;

    var el = {
      wrap: wrap,
      choice: wrap.querySelector(".hc-chat__lead-choice"),
      yes: wrap.querySelector(".hc-chat__lead-yes"),
      form: wrap.querySelector("form"),
      name: wrap.querySelector('[name="first_name"]'),
      email: wrap.querySelector('[name="email"]'),
      phone: wrap.querySelector('[name="phone"]'),
      consent: wrap.querySelector('[name="consent"]'),
      hp: wrap.querySelector('[name="website"]'),
      error: wrap.querySelector(".hc-chat__lead-error"),
      send: wrap.querySelector(".hc-chat__lead-send"),
      skip: wrap.querySelector("form .hc-chat__lead-skip"),
    };
    var sending = false;

    /** Shows the fields. focusName is false when the caller handles scroll and focus (details typed in the chat). */
    el.reveal = function (focusName) {
      if (!el.form.hidden) return;
      el.choice.hidden = true;
      el.form.hidden = false;
      if (!focusName) return;
      log.scrollTop += wrap.getBoundingClientRect().top - log.getBoundingClientRect().top - 8;
      el.name.focus();
    };

    el.yes.addEventListener("click", function () {
      el.reveal(true);
    });

    function showError(msg) {
      el.error.textContent = msg;
      el.error.hidden = !msg;
    }

    el.form.addEventListener("input", function () {
      if (!el.error.hidden) showError(""); // the shopper is fixing it
    });

    function finish(message, sent) {
      wrap.remove();
      lead = null;
      if (sent) {
        leadSent = true;
        storeSet(SENT_KEY, "1");
      }
      bubble(message, "bot");
    }

    var skips = wrap.querySelectorAll(".hc-chat__lead-skip"); // one in the choice row, one under the fields
    for (var i = 0; i < skips.length; i++) {
      skips[i].addEventListener("click", function () {
        finish("No problem. Ask me anything else.");
      });
    }

    el.form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (sending) return;

      var name = el.name.value.replace(/\s+/g, " ").trim();
      var email = el.email.value.trim();
      var phone = el.phone.value.trim();
      var digits = phone.replace(/\D/g, "");
      var ten = digits.length === 11 && digits.charAt(0) === "1" ? digits.slice(1) : digits;
      var problem =
        !name ? [el.name, "Please add your first name."] :
        !email ? [el.email, "Please add your email so the team can send the quote."] :
        email && !EMAIL_RE.test(email) ? [el.email, "That email address doesn't look right."] :
        phone && !/^[2-9]\d{2}[2-9]\d{6}$/.test(ten) // same rule as api/lead.ts toE164
          ? [el.phone, "Please enter a 10-digit US phone number."] :
        null;
      if (problem) {
        showError(problem[1]);
        problem[0].focus();
        return;
      }

      sending = true;
      el.send.disabled = true;
      el.skip.disabled = true;
      showError("");

      fetch(LEAD_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: name,
          email: email,
          phone: phone || null,
          consent_call_text: el.consent.checked,
          page_url: window.location.origin + window.location.pathname,
          product_title: productTitle() || null,
          transcript: history.slice(-24),
          website: el.hp.value,
        }),
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            return { ok: res.ok && data && data.ok === true, data: data };
          });
        })
        .then(function (r) {
          if (r.ok) {
            finish("Thanks, " + name + ". The team will follow up with your quote. " +
              "Ask me anything else in the meantime.", true);
            return;
          }
          showError((r.data && r.data.error) || LEAD_FAIL);
          sending = false;
          el.send.disabled = false;
          el.skip.disabled = false;
        })
        .catch(function () {
          showError(LEAD_FAIL);
          sending = false;
          el.send.disabled = false;
          el.skip.disabled = false;
        });
    });

    return el;
  }

  // ------------------------------------------------------- help prompt
  //
  // Once per visit, on a product page, after the page has been in view for 90
  // seconds, a small bubble above the launcher says NUDGE_TEXT, but only if the
  // chat was never opened this visit. Tapping the words opens the chat; the x
  // dismisses it for the rest of the visit. No offer, no name, nothing sent.
  // States: idle (not wanted) -> waiting (clock running) -> due (90 s passed,
  // waiting for a clear moment) -> shown -> done. While due or shown it is
  // re-checked twice a second and on scroll: it stays hidden in place while a
  // dialog, drawer or modal is open, or while it would cover a buy button.

  var NUDGE_AFTER_MS = 90000;
  var NUDGE_TEXT = "Need help choosing? Ask me anything.";
  var NUDGE_KEY = "hcChatNudgeShown";
  var OPENED_KEY = "hcChatOpened";
  /** The page's buy controls the bubble must never cover: Add to cart (also the sticky bar's), Shop Pay, the freight quote button. */
  var BUY_SELECTOR =
    'form[action*="/cart/add"] [type="submit"], button[type="submit"][form^="product-form"], ' +
    ".shopify-payment-button, .product-sticky-bar, .hc-quote-trigger";

  var chatOpened = storeGet(OPENED_KEY) === "1";
  var nudgeState = "idle";
  var nudgeTimer = null;
  var nudgeSince = 0;
  var nudgeTotal = 0;
  var nudgeTick = null;
  var nudgeFrame = 0;
  var nudgeWrap = null; // the bubble while it is on the page

  function startNudgeClock() {
    if (nudgeState !== "waiting" || nudgeTimer || document.hidden) return;
    nudgeSince = Date.now();
    nudgeTimer = setTimeout(function () {
      nudgeTimer = null;
      nudgeTotal += Date.now() - nudgeSince;
      nudgeState = "due";
      nudgeTick = setInterval(updateNudge, 500);
      window.addEventListener("scroll", onNudgeViewport, { passive: true });
      window.addEventListener("resize", onNudgeViewport);
      updateNudge();
    }, Math.max(0, NUDGE_AFTER_MS - nudgeTotal));
  }

  function pauseNudgeClock() {
    if (!nudgeTimer) return;
    clearTimeout(nudgeTimer);
    nudgeTimer = null;
    nudgeTotal += Date.now() - nudgeSince;
  }

  /** True while something sits over the page: a native dialog (modal or not), a theme drawer or modal, or the photo zoom. */
  function overlayOpen() {
    if (document.querySelector("[open]:not(details), .pswp--open")) return true;
    try {
      return !!document.querySelector(":modal");
    } catch (e) {
      return false; // older browser without :modal; [open] above already covers native dialogs
    }
  }

  function isShown(el) {
    if (typeof el.checkVisibility === "function") {
      return el.checkVisibility({ opacityProperty: true, visibilityProperty: true });
    }
    var cs = window.getComputedStyle(el);
    return cs.display !== "none" && cs.visibility !== "hidden" && parseFloat(cs.opacity || "1") > 0.05;
  }

  /** True when the box, widened by 8 px, touches any visible buy control. */
  function coversBuyButton(box) {
    var list = document.querySelectorAll(BUY_SELECTOR);
    for (var i = 0; i < list.length; i++) {
      var r = list[i].getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (r.right < box.left - 8 || r.left > box.right + 8 || r.bottom < box.top - 8 || r.top > box.bottom + 8) continue;
      if (isShown(list[i])) return true;
    }
    return false;
  }

  function buildNudge() {
    var wrap = document.createElement("div");
    wrap.className = "hc-chat__nudge hc-chat__nudge--off";
    // Static markup only; the words are set with textContent.
    wrap.innerHTML =
      '<button type="button" class="hc-chat__nudge-open"></button>' +
      '<button type="button" class="hc-chat__nudge-close" aria-label="Dismiss">×</button>';
    var words = wrap.querySelector(".hc-chat__nudge-open");
    words.textContent = NUDGE_TEXT;
    words.addEventListener("click", function () {
      open();
    });
    wrap.querySelector(".hc-chat__nudge-close").addEventListener("click", function () {
      endNudge(true);
    });
    root.insertBefore(wrap, launcher);
    return wrap;
  }

  function updateNudge() {
    if (nudgeState !== "due" && nudgeState !== "shown") return;
    if (chatOpened || root.classList.contains("hc-chat--open")) {
      endNudge(false);
      return;
    }
    if (!nudgeWrap) nudgeWrap = buildNudge();
    // Measured in place while hidden (visibility only), so the check never flashes it on screen.
    // Shown only while the launcher itself is visible: on phones the launcher steps aside near Add to cart.
    var clear = !document.hidden && isShown(launcher) && !overlayOpen() && !coversBuyButton(nudgeWrap.getBoundingClientRect());
    if (!clear) {
      if (nudgeWrap.contains(document.activeElement)) launcher.focus();
      nudgeWrap.classList.add("hc-chat__nudge--off");
      return;
    }
    nudgeWrap.classList.remove("hc-chat__nudge--off");
    if (nudgeState === "due") {
      nudgeState = "shown";
      storeSet(NUDGE_KEY, "1");
      nudgeWrap.classList.add("hc-chat__nudge--in");
    }
  }

  function onNudgeViewport() {
    if (nudgeFrame) return;
    nudgeFrame = window.requestAnimationFrame(function () {
      nudgeFrame = 0;
      updateNudge();
    });
  }

  /** Ends the prompt for this page: dismissed, the chat opened, or no longer wanted. */
  function endNudge(refocus) {
    nudgeState = "done";
    pauseNudgeClock();
    if (nudgeTick) {
      clearInterval(nudgeTick);
      nudgeTick = null;
    }
    window.removeEventListener("scroll", onNudgeViewport, { passive: true });
    window.removeEventListener("resize", onNudgeViewport);
    if (nudgeWrap) {
      var hadFocus = nudgeWrap.contains(document.activeElement);
      nudgeWrap.remove();
      nudgeWrap = null;
      if (refocus && hadFocus) launcher.focus();
    }
  }

  function startNudge() {
    if (chatOpened || storeGet(NUDGE_KEY) === "1" || !isProductPage()) return;
    nudgeState = "waiting";
    startNudgeClock();
  }

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) pauseNudgeClock();
    else startNudgeClock();
  });

  // ---------------------------------------------------------------- open

  var greeted = false;

  function open() {
    root.classList.add("hc-chat--open");
    if (!chatOpened) {
      chatOpened = true;
      storeSet(OPENED_KEY, "1");
    }
    endNudge(false);
    if (!greeted) {
      bubble(GREETING, "bot");
      greeted = true;
    }
    startAskClock();
    setTimeout(function () { input.focus(); }, 50);
  }

  function close() {
    root.classList.remove("hc-chat--open");
    pauseAskClock();
    launcher.focus();
  }

  launcher.addEventListener("click", open);
  closeBtn.addEventListener("click", close);

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (root.classList.contains("hc-chat--open")) close();
    else if (nudgeWrap && nudgeWrap.contains(document.activeElement)) endNudge(true);
  });

  // auto-grow the textarea; Enter sends, Shift+Enter newlines
  input.addEventListener("input", function () {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 96) + "px";
  });

  input.addEventListener("keydown", function (e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  });

  // ---------------------------------------------------------------- send

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (busy) return;

    var text = input.value.trim();
    if (!text) return;

    bubble(text, "user");
    history.push({ role: "user", content: text });
    var typed = parseContact(text);

    input.value = "";
    input.style.height = "auto";
    setBusy(true);
    var typing = showTyping();
    var answer = null;

    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: history }),
    })
      .then(function (res) {
        return res.json().then(function (data) {
          return { ok: res.ok, data: data };
        });
      })
      .then(function (r) {
        typing.remove();
        if (r.ok && r.data && r.data.reply) {
          bubble(r.data.reply, "bot");
          history.push({ role: "assistant", content: r.data.reply });
          answer = r.data.reply;
        } else {
          // Drop the unanswered user turn so the next send starts from a
          // consistent history the API will accept.
          history.pop();
          bubble((r.data && r.data.error) || OFFLINE, "error");
        }
      })
      .catch(function () {
        typing.remove();
        history.pop();
        bubble(OFFLINE, "error");
      })
      .then(function () {
        setBusy(false);
        input.focus();
        afterTurn(answer, typed);
      });
  });

  // ---------------------------------------------------------------- mount

  function mount() {
    document.body.appendChild(root);
    startNudge(); // the 90 s clock starts when the widget is on the page
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount);
  } else {
    mount();
  }
})();
